/**
 * Behaviour for guest / auth pages (vanilla JS, no framework).
 * Markup contracts: [data-toggle-password], [data-toast], [data-resend], [data-register],
 * [data-error-for], [data-confirm-submit], plus the language dropdown and busy submit buttons.
 * Globals provided by the page: route() (Ziggy), Swal (delete-account page only).
 */
const csrf = () => document.querySelector('meta[name="csrf-token"]').getAttribute('content');
const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

async function post(url, data) {
    const response = await fetch(url, {
        method: 'POST',
        headers: {'Accept': 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest'},
        body: JSON.stringify(data),
    });
    let body = null;
    try { body = await response.json(); } catch (e) { /* non-JSON */ }
    if (!response.ok) { const error = new Error('Request failed'); error.errors = body && body.errors ? body.errors : (body && body.message) || response.statusText; error.body = body; throw error; }
    return body;
}

/* ---------- Toasts ---------- */
const TONES = {success: 'bg-blue/10 text-blue', error: 'bg-red/10 text-red', warning: 'bg-line/40 text-ink', info: 'bg-surface text-ink'};
function showToast(message, type = 'error') {
    const region = document.getElementById('toast-region');
    if (!region) return;
    const el = document.createElement('div');
    el.setAttribute('data-toast', '');
    el.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');
    el.className = 'flex animate-fade-in-down items-start gap-3 rounded px-4 py-3 text-sm shadow-card ' + (TONES[type] || TONES.info);
    const text = document.createElement('div');
    text.className = 'flex-1';
    text.textContent = message;
    const close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('data-toast-close', '');
    close.setAttribute('aria-label', document.documentElement.dataset.closeLabel || 'Close');
    close.className = 'rounded px-2 hover:bg-ink/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-current';
    close.textContent = '×';
    el.append(text, close);
    region.append(el);
}
document.addEventListener('click', (event) => {
    const close = event.target.closest('[data-toast-close]');
    if (close) close.closest('[data-toast]').remove();
});

/* ---------- Password visibility ---------- */
document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-toggle-password]');
    if (!toggle || toggle.disabled) return;
    const input = document.getElementById(toggle.getAttribute('data-toggle-password'));
    const show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    toggle.setAttribute('aria-pressed', show ? 'true' : 'false');
    toggle.setAttribute('aria-label', show ? toggle.dataset.hideLabel || 'Hide password' : toggle.dataset.showLabel || 'Show password');
});

/* ---------- Busy state on native form posts ---------- */
document.addEventListener('submit', (event) => {
    window.setTimeout(() => {
        if (event.defaultPrevented) return;
        const button = event.target.querySelector('button[type="submit"]');
        if (!button) return;
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
    }, 0);
});
window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    $$('button[aria-busy="true"]').forEach((b) => { b.removeAttribute('aria-busy'); b.disabled = false; });
});

/* ---------- Language dropdown ---------- */
(function () {
    const button = document.getElementById('currentLanguage');
    const list = document.getElementById('languageList');
    if (!button || !list) return;
    const setOpen = (open) => {
        list.classList.toggle('hidden', !open);
        list.classList.toggle('flex', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    button.addEventListener('click', (event) => { event.stopPropagation(); setOpen(list.classList.contains('hidden')); });
    document.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') { setOpen(false); button.focus(); } });
})();

/* ---------- Confirm before submitting (delete account) ---------- */
$$('form[data-confirm-submit]').forEach((form) => {
    form.addEventListener('submit', (event) => {
        if (form.dataset.confirmed === '1') return;
        event.preventDefault();
        let config = {};
        try { config = JSON.parse(form.getAttribute('data-confirm-submit')); } catch (e) { /* use defaults */ }
        Swal.fire(Object.assign({icon: 'warning', showCancelButton: true, confirmButtonColor: '#b01f2a', cancelButtonColor: '#21232b'}, config)).then((result) => {
            if (result.isConfirmed) { form.dataset.confirmed = '1'; form.requestSubmit ? form.requestSubmit() : form.submit(); }
        });
    });
});

/* ---------- Verification code: resend countdown ---------- */
$$('[data-resend]').forEach((button) => {
    const label = button.dataset.label;
    const cooldown = parseInt(button.dataset.cooldown, 10) * 60;
    let remaining = parseInt(button.dataset.remaining, 10) || 0;
    let timer = null;
    const pad = (n) => String(n).padStart(2, '0');
    const render = () => {
        if (remaining > 0) {
            button.disabled = true;
            button.textContent = pad(Math.floor(remaining / 60)) + ':' + pad(remaining % 60);
        } else {
            button.disabled = false;
            button.textContent = label;
            clearInterval(timer);
        }
    };
    const start = (seconds) => {
        remaining = seconds;
        clearInterval(timer);
        timer = setInterval(() => { remaining -= 1; render(); }, 1000);
        render();
    };
    start(remaining);

    button.addEventListener('click', async () => {
        button.disabled = true;
        const payload = {setTime: true};
        const hash = document.querySelector('input[name=hash]');
        if (button.hasAttribute('data-send-hash') && hash) payload.hash = hash.value;
        try {
            const data = await post(button.dataset.url, payload);
            if (data) { start(cooldown); return; }
        } catch (e) { /* fall through to re-enable */ }
        render();
    });
});

/* ---------- Register: find account by personal ID, then submit as JSON ---------- */
$$('form[data-register]').forEach((form) => {
    const $ = (name) => form.querySelector('[name="' + name + '"]');
    const lookupButton = form.querySelector('[data-lookup]');
    const submit = form.querySelector('button[type="submit"]');
    const fields = $$('input, select', form).filter((el) => el.name !== 'personal_number');
    const setEnabled = (enabled) => { fields.forEach((el) => { el.disabled = !enabled; }); $$('[data-toggle-password]', form).forEach((b) => { b.disabled = !enabled; }); submit.disabled = !enabled; };
    const notInBilling = form.querySelector('[data-not-in-billing]');
    const fieldsPanel = form.querySelector('[data-register-fields]');
    const clearErrors = () => { $$('[data-error-for]', form).forEach((el) => { el.textContent = ''; }); if (notInBilling) notInBilling.hidden = true; };
    const setLoading = (button, on) => { button.disabled = on; button.setAttribute('aria-busy', on ? 'true' : 'false'); };
    const showErrors = (errors) => {
        if (typeof errors === 'string') return showToast(errors, 'error');
        Object.keys(errors).forEach((key) => {
            const target = form.querySelector('[data-error-for="' + key + '"]');
            const field = $(key);
            if (target) target.textContent = errors[key][0];
            if (field) field.setAttribute('aria-invalid', 'true');
        });
    };
    const data = () => Object.fromEntries(new FormData(form).entries());

    setEnabled(false);

    lookupButton.addEventListener('click', async () => {
        clearErrors();
        setLoading(lookupButton, true);
        try {
            const res = await post(form.dataset.lookupUrl, {personal_number: $('personal_number').value});
            $('email').value = res.email || '';
            $('firstname').value = res.firstname || '';
            $('lastname').value = res.lastname || '';
            const mobile = $('mobile');
            mobile.innerHTML = '';
            const placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = mobile.dataset.placeholder;
            mobile.append(placeholder);
            (res.phones || []).forEach((phone, index) => { const o = document.createElement('option'); o.value = index; o.textContent = phone; mobile.append(o); });
            setEnabled(true);
            if (fieldsPanel) { fieldsPanel.hidden = false; $('firstname').focus(); }
            setLoading(lookupButton, false);
        } catch (e) {
            showErrors(e.errors);
            if (fieldsPanel) { fieldsPanel.hidden = true; setEnabled(false); }
            if (notInBilling && e.body && e.body.code === 'not_in_billing') notInBilling.hidden = false;
            setTimeout(() => setLoading(lookupButton, false), 1000);
        }
    });

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        clearErrors();
        const payload = data(); // read before disabling: disabled fields are excluded from FormData
        setEnabled(false);
        submit.setAttribute('aria-busy', 'true');
        try {
            const res = await post(form.dataset.submitUrl, payload);
            if (res.success) { window.location.href = res.route; return; }
            throw {errors: {}};
        } catch (e) {
            showErrors(e.errors || {});
            setTimeout(() => { setEnabled(true); submit.removeAttribute('aria-busy'); }, 1000);
        }
    });
});
